/**
 * A utility class for handling localStorage operations with type safety and error handling
 */
export class LocalStorage {
  /**
   * Get an item from localStorage
   * @param key The key to retrieve
   * @returns The parsed value or null if not found
   */
  static get<T>(key: string): T | null {
    if (typeof window === 'undefined') return null

    try {
      const item = localStorage.getItem(key)
      if (!item) return null

      const data = JSON.parse(item)

      // Check if data has expiry and is expired
      if (data.expiry && new Date().getTime() > data.expiry) {
        localStorage.removeItem(key)
        return null
      }

      return data.value || null
    } catch (error) {
      console.error(`Error getting '${key}' from localStorage:`, error)
      return null
    }
  }

  /**
   * Save an item to localStorage
   * @param key The key to store under
   * @param value The value to store
   * @returns true if saved successfully, false otherwise
   */
  static set<T>(key: string, value: T): boolean {
    if (typeof window === 'undefined') return false

    try {
      const data = {
        value,
        expiry: null,
      }
      localStorage.setItem(key, JSON.stringify(data))
      return true
    } catch (error) {
      console.error(`Error saving '${key}' to localStorage:`, error)
      return false
    }
  }

  /**
   * Save an item to localStorage with expiry time
   * @param key The key to store under
   * @param value The value to store
   * @param expiryInMinutes Number of minutes until the item expires
   * @returns true if saved successfully, false otherwise
   */
  static setWithExpiry<T>(
    key: string,
    value: T,
    expiryInMinutes: number
  ): boolean {
    if (typeof window === 'undefined') return false

    try {
      const data = {
        value,
        expiry: new Date().getTime() + expiryInMinutes * 60 * 1000,
      }
      localStorage.setItem(key, JSON.stringify(data))
      return true
    } catch (error) {
      console.error(`Error saving '${key}' to localStorage:`, error)
      return false
    }
  }

  /**
   * Remove an item from localStorage
   * @param key The key to remove
   * @returns true if removed successfully, false otherwise
   */
  static remove(key: string): boolean {
    if (typeof window === 'undefined') return false

    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error(`Error removing '${key}' from localStorage:`, error)
      return false
    }
  }

  /**
   * Clear all items from localStorage
   * @returns true if cleared successfully, false otherwise
   */
  static clear(): boolean {
    if (typeof window === 'undefined') return false

    try {
      localStorage.clear()
      return true
    } catch (error) {
      console.error('Error clearing localStorage:', error)
      return false
    }
  }
  /**
   * Clear all items from localStorage except for a specific key
   * @param key The key to exclude from clearing
   * @returns true if cleared successfully, false otherwise
   */
  static clearWithoutKey(key: string): boolean {
    if (typeof window === 'undefined') return false

    try {
      const savedValue = localStorage.getItem(key)
      localStorage.clear()
      if (savedValue) {
        localStorage.setItem(key, savedValue)
      }
      return true
    } catch (error) {
      console.error(`Error clearing localStorage except key '${key}':`, error)
      return false
    }
  }

  /**
   * Check if a key exists in localStorage and is not expired
   * @param key The key to check
   * @returns true if key exists and not expired, false otherwise
   */
  static has(key: string): boolean {
    if (typeof window === 'undefined') return false

    try {
      const item = localStorage.getItem(key)
      if (!item) return false

      const data = JSON.parse(item)
      if (data.expiry && new Date().getTime() > data.expiry) {
        localStorage.removeItem(key)
        return false
      }

      return true
    } catch {
      return false
    }
  }
}
